import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatMerchandiseType } from '../../utils/formatters';
import {
  Clock,
  Navigation,
  CheckCircle2,
  MapPin,
  Package,
  ChevronRight,
  RefreshCw,
  Truck,
  Calendar,
  Wallet
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DriverMissions = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchMissions = useCallback(async (status) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/driver/missions?status=${status}&limit=50`,
        {}
      );
      setMissions(response.data.missions || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching missions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMissions(activeTab);
  }, [activeTab, fetchMissions]);

  const getStatusBadge = (status, driverConfirmed) => {
    const statusConfig = {
      planifiee: { 
        label: driverConfirmed ? 'Acceptée' : 'Nouvelle', 
        class: driverConfirmed ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800' 
      },
      prete_au_chargement: { label: 'Au chargement', class: 'bg-teal-100 text-teal-800' },
      en_cours_de_chargement: { label: 'Chargement', class: 'bg-orange-100 text-orange-800' },
      charge_en_route: { label: 'En route', class: 'bg-emerald-100 text-emerald-800' },
      livree: { label: 'Livrée', class: 'bg-green-100 text-green-800' },
      terminee: { label: 'Terminée', class: 'bg-gray-100 text-gray-800' },
      payee: { label: 'Payée', class: 'bg-purple-100 text-purple-800' }
    };
    const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const MissionCard = ({ mission }) => (
    <Link to={`/chauffeur/missions/${mission.id}`}>
      <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-semibold text-gray-900">{mission.operationNumber}</p>
            {mission.scheduledLoadingDate && (
              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                <Calendar className="w-3 h-3" />
                {new Date(mission.scheduledLoadingDate).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </p>
            )}
          </div>
          {getStatusBadge(mission.status, mission.driverConfirmed)}
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
          <MapPin className="w-4 h-4 text-green-500" />
          <span className="truncate">{mission.departure}</span>
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <MapPin className="w-4 h-4 text-red-500" />
          <span className="truncate">{mission.arrival}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3 text-gray-500">
            <span className="flex items-center gap-1">
              <Package className="w-4 h-4" />
              {formatMerchandiseType(mission.merchandiseType)}
            </span>
            <span>{mission.weight}T</span>
            {mission.vehiclePlate && (
              <span className="flex items-center gap-1">
                <Truck className="w-4 h-4" />
                {mission.vehiclePlate}
              </span>
            )}
          </div>
          {mission.driverPayment && (
            <span className="font-semibold text-[#1db584] flex items-center gap-1">
              <Wallet className="w-4 h-4" />
              {formatCurrency(mission.driverPayment)}
            </span>
          )}
        </div>
      </Card>
    </Link>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg md:text-2xl font-bold text-gray-900">Mes Missions</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchMissions(activeTab)}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            En attente
          </TabsTrigger>
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Navigation className="w-4 h-4" />
            En cours
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Terminées
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-[#1db584]" />
            </div>
          ) : missions.length === 0 ? (
            <Card className="p-8 text-center">
              <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">
                {activeTab === 'pending' && 'Aucune mission en attente'}
                {activeTab === 'active' && 'Aucune mission en cours'}
                {activeTab === 'completed' && 'Aucune mission terminée'}
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-4">{total} mission(s)</p>
              {missions.map((mission) => (
                <MissionCard key={mission.id} mission={mission} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DriverMissions;
